/**
 * useAppPermissions — App-level RBAC hook for AxonBOS V2.
 *
 * Provides role-based access control using the 4-role capability matrix:
 * Super Admin > Admin > Manager > Viewer
 *
 * Components use boolean flags to conditionally render (hide, not grey) UI elements.
 *
 * U0.P4 — RBAC Visibility System
 */

import * as React from 'react';

// ─── Types ──────────────────────────────────────────────────────────

export type AppRole = 'super_admin' | 'admin' | 'manager' | 'viewer';

export interface AppUser {
  id: string;
  email: string;
  role: AppRole;
  company_id: number;
  name?: string;
  avatar?: string;
}

export interface AppPermissions {
  /** Current user role */
  role: AppRole;
  /** Can see Admin panel (super_admin only) */
  canViewAdmin: boolean;
  /** Can see all Settings tabs (super_admin, admin) */
  canViewAllSettings: boolean;
  /** Can see own profile settings (all roles) */
  canViewProfile: boolean;
  /** Can add/remove connectors (super_admin, admin) */
  canManageConnectors: boolean;
  /** Can edit data records (super_admin, admin, manager) */
  canEditData: boolean;
  /** Can delete data records (super_admin, admin) */
  canDeleteData: boolean;
  /** Can view data records (all roles) */
  canViewData: boolean;
  /** Can create Evolution rules (super_admin, admin) */
  canCreateRules: boolean;
  /** Can view Evolution rules (all roles) */
  canViewRules: boolean;
  /** Can view all audit entries (super_admin, admin) */
  canViewAllAudit: boolean;
  /** Can view own audit entries (all roles) */
  canViewOwnAudit: boolean;
  /** Can use chat (all roles) */
  canChat: boolean;
  /** Generic permission check by action name */
  can: (action: string) => boolean;
}

// ─── Capability Matrix ──────────────────────────────────────────────

const ROLE_HIERARCHY: AppRole[] = ['super_admin', 'admin', 'manager', 'viewer'];

/**
 * Map of action → minimum roles that have the capability.
 */
const CAPABILITY_MAP: Record<string, AppRole[]> = {
  'admin.view': ['super_admin'],
  'settings.all': ['super_admin', 'admin'],
  'settings.profile': ['super_admin', 'admin', 'manager', 'viewer'],
  'connectors.manage': ['super_admin', 'admin'],
  'data.edit': ['super_admin', 'admin', 'manager'],
  'data.delete': ['super_admin', 'admin'],
  'data.view': ['super_admin', 'admin', 'manager', 'viewer'],
  'evolution.create': ['super_admin', 'admin'],
  'evolution.view': ['super_admin', 'admin', 'manager', 'viewer'],
  'audit.all': ['super_admin', 'admin'],
  'audit.own': ['super_admin', 'admin', 'manager', 'viewer'],
  'chat': ['super_admin', 'admin', 'manager', 'viewer'],
};

// ─── Auth Context ───────────────────────────────────────────────────

interface AuthContextValue {
  user: AppUser | null;
  setUser: (user: AppUser | null) => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export interface AppAuthProviderProps {
  /** Initial user (optional, for SSR or pre-loaded auth) */
  initialUser?: AppUser | null;
  children: React.ReactNode;
}

/**
 * AppAuthProvider — Provides auth context to the app.
 * Wrap your root app with this provider.
 */
export function AppAuthProvider({ initialUser = null, children }: AppAuthProviderProps) {
  const [user, setUser] = React.useState<AppUser | null>(initialUser);

  const value = React.useMemo(() => ({ user, setUser }), [user]);

  return React.createElement(AuthContext.Provider, { value }, children);
}

/**
 * useAppAuth — Access the current authenticated user.
 */
export function useAppAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAppAuth must be used within an AppAuthProvider');
  }
  return ctx;
}

// ─── Permission Hook ────────────────────────────────────────────────

/**
 * useAppPermissions — Returns role-based permission flags for the current user.
 *
 * Usage:
 *   const perms = useAppPermissions();
 *   {perms.canEditData && <button>Edit</button>}
 *   {perms.canViewAdmin && <NavItem>Admin</NavItem>}
 */
export function useAppPermissions(): AppPermissions {
  const { user } = useAppAuth();
  const role: AppRole = user?.role ?? 'viewer';

  const can = React.useCallback(
    (action: string): boolean => {
      const allowedRoles = CAPABILITY_MAP[action];
      if (!allowedRoles) return false;
      return allowedRoles.includes(role);
    },
    [role],
  );

  return React.useMemo<AppPermissions>(
    () => ({
      role,
      canViewAdmin: role === 'super_admin',
      canViewAllSettings: ['super_admin', 'admin'].includes(role),
      canViewProfile: true,
      canManageConnectors: ['super_admin', 'admin'].includes(role),
      canEditData: ['super_admin', 'admin', 'manager'].includes(role),
      canDeleteData: ['super_admin', 'admin'].includes(role),
      canViewData: true,
      canCreateRules: ['super_admin', 'admin'].includes(role),
      canViewRules: true,
      canViewAllAudit: ['super_admin', 'admin'].includes(role),
      canViewOwnAudit: true,
      canChat: true,
      can,
    }),
    [role, can],
  );
}
